/**
 * SignUp.jsx
 *
 * Registration page for creating a new user account.
 * Handles form input, validation, and communication with the backend API.
 *
 * Features:
 * - Collects username, email, and password
 * - Validates password match and minimum length
 * - Sends registration request to backend
 * - Stores returned user data in localStorage
 * - Redirects user to home page upon successful signup
 *
 * Dependencies:
 * - react-router-dom (navigation + linking)
 * - Backend API (/api/users/register)
 *
 * Notes:
 * - Uses basic client-side validation before submission
 * - Backend is responsible for enforcing additional validation and security
 * - Session/token handling depends on backend implementation
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import '../css/LoginForm.css';
import logo from '../assets/logo.png';


const SignUp = () => {

    // Navigation hook for redirecting after successful signup
    const navigate = useNavigate();

    // Form state for user input fields
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        confirmPassword: ''
    });

    // UI state for error handling and loading state
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    /**
     * handleChange(e)
     *
     * Updates form state dynamically based on input field name
     */
    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

     /**
     * handleSubmit(e)
     *
     * Handles form submission for user registration.
     * Performs client-side validation before sending request.
     *
     * Validation:
     * - Passwords must match
     * - Password must meet minimum length requirement (6 characters)
     *
     * On success:
     * - Stores user data in localStorage
     * - Redirects to /home
     *
     * On failure:
     * - Displays error message to user
     */
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        console.log("Submitted")

        // check if passwords match
        if (formData.password !== formData.confirmPassword) {
            setError("Passwords do not match");
            return;
        }

        //check pass length
        if (formData.password.length < 6) {
            setError("Password must be at least 6 characters");
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/register`, {
            //const response = await fetch('http://localhost:5001/api/users/register', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    username: formData.username,
                    email: formData.email,
                    password: formData.password
                }),
            });

            // Handle unsuccessful response
            if (!response.ok) {
                throw new Error('Sign Up failed');
            }   
            const data = await response.json();

            /**
             * Store user data locally for client-side access
             * (authentication still relies on backend/session if implemented)
             */
            localStorage.setItem('user', JSON.stringify(data.user));

            // Redirect to home page after successful registration
            navigate('/home');
        } catch (err) {
            setError("Sign Up failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-container">
            {/* Application logo */}
            <img src={logo} alt="logo" className="login-logo" />
            <h1>Create Account with G&C</h1>

            {/* Registration Form */}
            <form className="login-form" onSubmit={handleSubmit}>
                <label>Username:</label>
                <input type="text" placeholder="Enter your username" value={formData.username} onChange={handleChange} name="username" required />

                <label>Email:</label>
                <input type="email" placeholder="Enter your email" value={formData.email} onChange={handleChange} name="email" required />

                <label>Password:</label>
                <input type="password" placeholder="Enter your password" value={formData.password} onChange={handleChange} name="password" required />

                <label>Confirm Password:</label>
                <input type="password" placeholder="Confirm your password" value={formData.confirmPassword} onChange={handleChange} name="confirmPassword" required />

                {/* Submit Button */}
                <button type="submit" className="login-button" disabled={loading}>
                    Sign Up
                </button>
            
            {/* Navigation back to login */}
            <p className="signup-link">
                Already have an account? <strong><Link to="/">Back to Login</Link></strong>
            </p>
            </form>
        </div>
    );
}

export default SignUp;